/**
 * M2 — Mapa interativo.
 * Mapa Leaflet com pins dos instrumentos (cor por nível, forma por tipo)
 * + tabela agrupada por tipo abaixo. Clique na linha destaca o pin.
 *
 * GUPM-194 (fiel ao V8): barra de controles ACIMA do mapa (barragem + tipos de
 * mapa + camadas); legenda-filtro dos pins (nível × tipo) ABAIXO — começa com
 * tudo marcado.
 */
import 'leaflet/dist/leaflet.css';
import './map.css';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmailSuporteApp } from '@/hooks/useEmailSuporteApp';
import type { InstrumentType } from '@/domain';
import type { NivelExibido } from '@/domain/instrumentos/nivel-exibido';
import InstrumentMap from './components/InstrumentMap';
import MapControls from './components/MapControls';
import MapLegend, { type MapLayer } from './components/MapLegend';
import MapStatusBanners from './components/MapStatusBanners';
import PaebmZasPanel from './components/PaebmZasPanel';
import { useMapaData } from './hooks/useMapaData';
import { useMancha } from './hooks/useMancha';
import { usePaebmMapa } from './hooks/usePaebmMapa';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';
import {
  buildMapPins, filterInstruments, groupInstrumentsByType, instrumentosSemPino,
  NIVEL_EXIBIDO_ORDER, TYPE_ORDER,
} from './domain/pins';
import { sirenePoints } from './domain/paebm';
import type { BasemapMode } from './domain/basemaps';
import { PageHeader } from '@/ui';
import ThresholdsPorEstrutura from '@/components/instrumentos/ThresholdsPorEstrutura';
import { useFaixasCanal } from '@/hooks/useFaixasCanal';
import {
  atalhoSemFluxoNavState, nivelParaAtalho, resolverAtalhoDeFluxo,
} from '@/domain/tarp/disparo';
import { useDams } from '@/hooks/useDams';
import { useDamScope } from '@/hooks/useDamScope';
import {
  ALL_DAMS, countInstrumentsByDam, filterInstrumentsByDam, type DamSelection,
} from '@/domain/instrumentos/dam-filter';

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export default function M2Mapa() {
  const navigate = useNavigate();
  const { instruments, readings, isLoading, error } = useMapaData();
  // GUPM-336: contato do suporte na tela de falha (vem do banco).
  const emailSuporte = useEmailSuporteApp();
  // GUPM-270 — régua por eixo do tiltímetro, para a tabela dedicada do painel.
  const { faixasCanal } = useFaixasCanal();
  const { data: dams = [] } = useDams();
  // Status por barragem — MESMA fonte do header (useDamScope): o filtro do mapa
  // vira um mini-semáforo coerente com os chips A/B/C do topo (GUPM-194).
  const { dams: damStatus } = useDamScope();
  const statusByDam = useMemo(
    () => Object.fromEntries(damStatus.map((d) => [d.id, d.status.nivel] as const)),
    [damStatus],
  );
  const [selectedDam, setSelectedDam] = useState<DamSelection>(ALL_DAMS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Nº de pedidos de foco — ver `focusSeq` no InstrumentMap (reclique na lista). */
  const [focusSeq, setFocusSeq] = useState(0);
  const mapaRef = useRef<HTMLDivElement>(null);
  const [basemap, setBasemap] = useState<BasemapMode>('satellite');
  // GUPM-228 — o filtro inclui "sem leitura": sem ele o instrumento sem leitura
  // não casaria com nenhum dos 4 níveis e sumiria do mapa.
  const [activeLevels, setActiveLevels] = useState<ReadonlySet<NivelExibido>>(
    () => new Set(NIVEL_EXIBIDO_ORDER),
  );
  const [activeTypes, setActiveTypes] = useState<ReadonlySet<InstrumentType>>(() => new Set(TYPE_ORDER));
  const [layers, setLayers] = useState<Record<MapLayer, boolean>>({
    mancha: true, sirenes: true, edificacoes: true, pontosEncontro: true, zss: false,
  });

  // Sirenes (demo do protótipo) + edificações/pontos de encontro (banco, GUPM-195) + mancha oficial.
  const allSirenes = useMemo(() => sirenePoints(), []);
  const { edificacoes, pontosEncontro, error: paebmError } = usePaebmMapa();
  const { mancha, bounds: manchaBounds, error: manchaError } = useMancha();

  // GUPM-134 — recorte por estrutura (Barragem A/B/C). Contagem das pills sobre o
  // dataset completo (estável ao mexer na legenda); o resto da tela opera sobre `byDam`.
  const damCounts = useMemo(
    () => countInstrumentsByDam(instruments, dams.map((d) => d.id)),
    [instruments, dams],
  );
  const byDam = useMemo(
    () => filterInstrumentsByDam(instruments, selectedDam),
    [instruments, selectedDam],
  );
  const selectedDamCode = selectedDam === ALL_DAMS
    ? null
    : dams.find((d) => d.id === selectedDam)?.code ?? null;
  const damVazia = selectedDam !== ALL_DAMS && byDam.length === 0;

  const presentTypes = useMemo(
    () => groupInstrumentsByType(byDam).map((g) => g.type),
    [byDam],
  );
  const filtered = useMemo(
    () => filterInstruments(byDam, readings, activeLevels, activeTypes),
    [byDam, readings, activeLevels, activeTypes],
  );
  const pins = useMemo(() => buildMapPins(filtered, readings), [filtered, readings]);
  // GUPM-227 — sobre o dataset da barragem selecionada (não `filtered`, senão desmarcar
  // um tipo na legenda viraria "pendência de coordenada"). Nomeia os ativos sem pino;
  // vazio quando todos pinam. Substituiu o booleano do GUPM-129, que só acusava o caso
  // de ZERO pinos e ficava calado quando faltava coordenada só de alguns.
  const semPino = useMemo(() => instrumentosSemPino(byDam), [byDam]);

  // Atalho "→ Fluxo" do Resumo Técnico (GUPM-230): mesmo contrato do Dashboard —
  // contexto efêmero via router state. C-88: resolve pelo ID (o código se repete entre
  // barragens), usa o nível do PIOR canal com faixa (a primeira leitura do array podia ser
  // a temperatura do tiltímetro) e, sem fluxo a abrir, leva o motivo à tela de TARP.
  const abrirFluxo = (instrumentId: string) => {
    const r = resolverAtalhoDeFluxo(instrumentId, instruments, (i) => nivelParaAtalho(i, readings));
    if (r.ok) {
      navigate('/tarp', { state: { tarpAutoOpen: r.ctx } });
      return;
    }
    console.warn('[TARP] atalho sem fluxo', { instrumentId, motivo: r.motivo });
    navigate('/tarp', { state: atalhoSemFluxoNavState(r.motivo, r.instrumentCode) });
  };

  /**
   * Clique na linha da lista: sobe até o mapa, seleciona e pede foco (GUPM-230).
   *
   * O scroll é a parte não óbvia — a lista tem 84 linhas e fica ABAIXO do mapa, então
   * o `flyTo` e o popup aconteciam fora da área visível e o clique parecia inerte.
   * Clicar no PINO não passa por aqui: ali o mapa já está à vista e o Leaflet abre o
   * popup sozinho.
   */
  const focarNoMapa = (id: string) => {
    setSelectedId(id);
    setFocusSeq((n) => n + 1);
    mapaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <PageHeader
        title="Mapa"
        subtitle={`${rotuloDaEstrutura(selectedDamCode) ?? 'Todas as estruturas'} — instrumentos geotécnicos (UTM 23S SIRGAS2000 → WGS84)`}
      />

      {/* M2 não renderiza BannerArea de propósito (GUPM-194): o único banner que
          mirava o mapa era o R-E01 "Calibração NA" (regulatório, RISR 2026 Rec.#2),
          removido daqui a pedido do produto. Segue visível em M1/M6 (modulo_alvo). */}

      <MapStatusBanners
        isLoading={isLoading}
        error={error}
        emailSuporte={emailSuporte}
        showMancha={layers.mancha && !!manchaError}
        showPaebm={(layers.edificacoes || layers.pontosEncontro) && !!paebmError}
        semPino={semPino}
      />

      {!isLoading && !error && (
        <>
          {/* Barra de controles ACIMA do mapa (fiel ao V8): barragem + tipos de mapa + camadas. */}
          <MapControls
            dams={dams}
            damCounts={damCounts}
            selectedDam={selectedDam}
            onSelectDam={setSelectedDam}
            statusByDam={statusByDam}
            basemap={basemap}
            onBasemap={setBasemap}
            layers={layers}
            onToggleLayer={(layer) => setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }))}
          />
          {/* `ref` no wrapper para a lista poder rolar até aqui (ver `focarNoMapa`). */}
          <div ref={mapaRef}>
            <InstrumentMap
              pins={pins}
              selectedId={selectedId}
              focusSeq={focusSeq}
              onSelect={setSelectedId}
              basemap={basemap}
              sirenes={layers.sirenes ? allSirenes : []}
              edificacoes={layers.edificacoes ? edificacoes : []}
              pontosEncontro={layers.pontosEncontro ? pontosEncontro : []}
              mancha={layers.mancha ? mancha : null}
              manchaBounds={layers.mancha ? manchaBounds : null}
              zss={layers.zss}
            />
          </div>
          {damVazia && (
            <p
              role="status"
              className="mt-2 text-xs text-jf-muted rounded-lg border border-jf-border bg-jf-surface/60 px-3 py-2"
            >
              Sem instrumentos carregados para {rotuloDaEstrutura(selectedDamCode) ?? 'a estrutura selecionada'}.
            </p>
          )}
          {/* Legenda-filtro dos pins ABAIXO do mapa (níveis × tipos). */}
          <MapLegend
            presentTypes={presentTypes}
            activeLevels={activeLevels}
            activeTypes={activeTypes}
            onToggleLevel={(l) => setActiveLevels((prev) => toggle(prev, l))}
            onToggleType={(t) => setActiveTypes((prev) => toggle(prev, t))}
            onSetAllLevels={(on) => setActiveLevels(on ? new Set(NIVEL_EXIBIDO_ORDER) : new Set())}
            onSetAllTypes={(on) => setActiveTypes(on ? new Set(TYPE_ORDER) : new Set())}
          />
          {/* GUPM-230 — mesma lista organizada do Dashboard (Resumo Técnico por
              Estrutura), a pedido do dono. `modo="mapa"` carrega as diferenças
              desta tela (ver `PainelModo`): sem gate de perfil, sem barra de
              filtros própria, sem título, e instrumento sem Carta virando linha
              clicável. `onSelect` liga a lista ao mapa — a linha sobe até o mapa,
              voa e abre o card do pino. */}
          <div className="mt-4">
            <ThresholdsPorEstrutura
              instruments={filtered}
              readings={readings}
              dams={dams}
              faixasCanal={faixasCanal}
              onFluxo={abrirFluxo}
              onSelect={focarNoMapa}
              selectedId={selectedId}
              modo="mapa"
            />
          </div>

          {/* GUPM-195: lista de edificações ZAS + pontos de encontro (fim da tela,
              após a lista de instrumentos — referência de UI do protótipo V5_4_4).
              Dados reais do V8, sem PII (edificações sem moradores fixos).
              Independente dos toggles de camada (como a InstrumentTable): a lista é o
              cadastro completo; o toggle só liga/desliga os marcadores no mapa. */}
          <PaebmZasPanel edificacoes={edificacoes} pontosEncontro={pontosEncontro} />
        </>
      )}
    </div>
  );
}
